import { Box, Button, Grid, Stack, Typography, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { environment } from '../../../environments/environment';
import OrderStatusBadge from './OrderStatusBadge';
import { formatPriceFromCents } from '../../Utils/UtilsFunctions';
import { formatToLocalTime } from '../../Utils/UtilsFunctions';
import logo from '../../../assets/fattyBattyLogo.png';
import { firestore } from '../../../../../../libs/firebase-service/src';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FeesDialog from '../Dialouge/FeesDailoge';
const PICKUP_ORDER_STEPS = [
  {
    key: 'placed',
    label: 'Order Placed',
    content: 'Your order has been placed and is being processed.',
  },
  {
    key: 'inprogress',
    label: 'In Progress',
    content: 'Your order is being prepared and will be ready soon.',
  },
  {
    key: 'ready',
    label: 'Ready',
    content: 'Your order is ready for pickup.',
  },
  {
    key: 'delivered',
    label: 'Completed',
    content: 'Your order has been successfully picked up.',
  },
];

const DELIVERY_ORDER_STEPS = [
  {
    key: 'scheduled_pending',
    label: 'Scheduled',
    content:
      'Your delivery has been scheduled and will be processed on the chosen date.',
  },
  {
    key: 'created',
    label: 'Created',
    content: 'Your order has been created and is awaiting processing.',
  },
  {
    key: 'assigned',
    label: 'Assigned',
    content: 'Your order has been assigned to a delivery driver.',
  },
  {
    key: 'batched',
    label: 'Batched',
    content: 'Your order has been batched for efficient delivery.',
  },
  {
    key: 'awaiting_pickup',
    label: 'Awaiting Pickup',
    content: 'Your order is ready and awaiting pickup by the driver.',
  },
  {
    key: 'pickedup',
    label: 'Picked Up',
    content: 'Your order has been picked up and is on the way.',
  },
  {
    key: 'arrived_at_destination',
    label: 'Arrived',
    content: 'The driver has arrived at your destination.',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    content: 'Your order has been successfully delivered.',
  },
];

function getCurrentStep(order_status, deliveryOption, isScheduling) {
  if (deliveryOption === 'delivery') {
    // If scheduling is disabled for this order, treat the delivery steps
    // without the initial 'scheduled_pending' step (shift indices down by 1).
    if (isScheduling === false) {
      switch (order_status) {
        case 'created':
          return 0; // Created (first when no scheduling)
        case 'assigned':
          return 1; // Assigned
        case 'batched':
          return 2; // Batched
        case 'awaiting_pickup':
          return 3; // Awaiting Pickup
        case 'pickedup':
          return 4; // Picked Up
        case 'arrived_at_destination':
          return 5; // Arrived
        case 'delivered':
          return 6; // Delivered
        case 'cancelled':
          return 0; // Reset to beginning for cancelled orders
        default:
          return 0;
      }
    }

    // Delivery order status mapping when scheduling is enabled/possible
    switch (order_status) {
      case 'scheduled_pending':
        return 0; // Scheduled (new first step)
      case 'created':
        return 1; // Created (shifted)
      case 'assigned':
        return 2; // Assigned (shifted)
      case 'batched':
        return 3; // Batched (shifted)
      case 'awaiting_pickup':
        return 4; // Awaiting Pickup (shifted)
      case 'pickedup':
        return 5; // Picked Up (shifted)
      case 'arrived_at_destination':
        return 6; // Arrived (shifted)
      case 'delivered':
        return 7; // Delivered (shifted)
      case 'cancelled':
        return 0; // Reset to beginning for cancelled orders
      default:
        return 0;
    }
  } else {
    // Pickup order status mapping (original logic)
    switch (order_status) {
      case 'new':
      case 'pending':
      case 'confirmed':
        return 0; // Order Placed
      case 'inprogress':
        return 1; // In Progress
      case 'ready_to_pickup':
        return 2; // Ready
      case 'delivered':
        return 3; // Completed
      case 'cancelled':
        return 0; // Reset to beginning for cancelled orders
      default:
        return 0;
    }
  }
}

// Friendly payment method formatter - handles array (e.g. ["GIFT_CARD", "GOOGLE_PAY"]) or string
function formatPaymentMethod(pm) {
  if (!pm) return 'N/A';
  const methods = Array.isArray(pm) ? pm : [pm];
  const normalized = methods
    .map((m) => String(m).toUpperCase().trim())
    .filter(Boolean);
  if (normalized.length === 0) return 'N/A';

  const hasGiftCard = normalized.some(
    (m) => m === 'GIFT_CARD' || m.includes('GIFT')
  );
  const hasCard = normalized.some((m) => m === 'CARD' || m === 'CARD_ON_FILE');
  const hasGooglePay = normalized.some((m) => m.includes('GOOGLE'));
  const hasApplePay = normalized.some((m) => m.includes('APPLE'));
  const hasCashApp = normalized.some((m) => m.includes('CASH'));

  if (hasGiftCard && hasCard) return 'Card + Gift Card';
  if (hasGiftCard && hasGooglePay) return 'Gift Card + Google Pay';
  if (hasGiftCard && hasApplePay) return 'Gift Card + Apple Pay';
  if (hasGiftCard && hasCashApp) return 'Gift Card + Cash App Pay';

  if (normalized.includes('CARD_ON_FILE')) return 'Card (On File)';
  if (hasCard) return 'Card';
  if (hasGiftCard) return 'Gift Card';
  if (hasGooglePay) return 'Google Pay';
  if (hasApplePay) return 'Apple Pay';
  if (hasCashApp) return 'Cash App Pay';

  const fallback = normalized[0];
  return String(fallback)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Format gift card number as **** **** **** last4
function formatGiftCardMasked(last4) {
  if (!last4) return '**** **** **** ****';
  const l4 = String(last4).slice(-4);
  return `**** **** **** ${l4}`;
}

const OrderHistoryDetails = ({ order, onBack }) => {
  const [liveOrder, setLiveOrder] = useState(order);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [openTaxesDialog, setOpenTaxesDialog] = useState(false);
  const {
    order_id,
    order_status,
    order_items = [],
    order_total,
    created_at,

    deliveryOption,
    payment_method,
    estimated_delivery_date,
    fulfillments = [],
  } = order;
  console.log(payment_method, 'payment_method');
  console.log(order, 'order');

  // Friendly payment method to display (use 'Cash on delivery' when unknown)
  const _formattedPaymentMethod = formatPaymentMethod(payment_method);
  const displayPaymentMethod =
    _formattedPaymentMethod === 'N/A'
      ? 'Cash on delivery'
      : _formattedPaymentMethod;

  // Live listen to Firestore order doc to update status in real-time
  useEffect(() => {
    if (!order_id) return;
    try {
      // Prime with a one-time fetch to avoid initial incorrect status flicker
      firestore
        .collection('orders')
        .doc(order_id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            setLiveOrder(doc.data());
          }
          setStatusLoaded(true);
        })
        .catch((err) => {
          // Error handled silently
          setStatusLoaded(true);
        });

      const unsubscribe = firestore
        .collection('orders')
        .doc(order_id)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              setLiveOrder(doc.data());
            }
            setStatusLoaded(true);
          },
          (err) => {
            // Error handled silently
            setStatusLoaded(true);
          }
        );
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    } catch (e) {
      // Error handled silently
      setStatusLoaded(true);
    }
  }, [order_id]);

  // Function to get the appropriate status based on order type
  const getOrderStatus = () => {
    const src = liveOrder || order;
    // For delivery orders, prioritize order_delivery_status if available
    if (src?.deliveryOption === 'delivery' && src?.order_delivery_status) {
      return src.order_delivery_status;
    }

    // For pickup orders or if no delivery status, use Square fulfillments or order_status
    if (src?.fulfillments && src.fulfillments.length > 0) {
      return mapSquareStatusToCustomStatus(src.fulfillments);
    }

    return src?.order_status ?? order_status;
  };

  // Function to map Square fulfillment states to our custom status
  const mapSquareStatusToCustomStatus = (fulfillments) => {
    if (!fulfillments || fulfillments.length === 0) {
      return order_status; // Fallback to existing order_status
    }

    // Get the first fulfillment (assuming single fulfillment per order)
    const fulfillment = fulfillments[0];
    const squareState = fulfillment?.state;

    switch (squareState) {
      case 'PROPOSED':
        return 'new'; // Order Placed
      case 'RESERVED':
        return 'inprogress'; // In Progress
      case 'PREPARED':
        return 'ready_to_pickup'; // Ready
      case 'COMPLETED':
        return 'delivered'; // Delivered
      case 'CANCELED':
        return 'cancelled'; // Cancelled
      case 'FAILED':
        return 'inprogress'; // In Progress (or could be 'failed' if you add this state)
      default:
        return order_status; // Fallback to existing order_status
    }
  };

  // Check if refunds object exists
  const activeOrder = liveOrder || order;
  const hasRefunds =
    activeOrder?.refunds && Object.keys(activeOrder.refunds).length > 0;

  // Get the appropriate status and current step
  const mappedStatus = getOrderStatus();

  // Treat cancelled/canceled equivalently and also check raw order fields for safety
  const isCancelled =
    mappedStatus === 'cancelled' ||
    mappedStatus === 'canceled' ||
    (liveOrder?.order_status ?? order_status) === 'cancelled' ||
    (liveOrder?.order_delivery_status ?? order?.order_delivery_status) ===
      'cancelled' ||
    ((liveOrder?.fulfillments ?? fulfillments) &&
      (liveOrder?.fulfillments ?? fulfillments)[0]?.state === 'CANCELED');

  // Determine whether this order was scheduled; default to undefined (treated as true)
  const isScheduling = liveOrder?.is_scheduling ?? order?.is_scheduling;

  const currentStep = getCurrentStep(
    mappedStatus,
    deliveryOption,
    isScheduling
  );

  // If scheduling is explicitly false, remove the 'scheduled_pending' step for delivery
  let ORDER_STEPS;
  if (deliveryOption === 'delivery') {
    ORDER_STEPS =
      isScheduling === false
        ? DELIVERY_ORDER_STEPS.filter((s) => s.key !== 'scheduled_pending')
        : DELIVERY_ORDER_STEPS;
  } else {
    ORDER_STEPS = PICKUP_ORDER_STEPS;
  }

  // Status display and styles are provided by the shared OrderStatusBadge component

  return (
    <Stack
      sx={{
        width: '100%',
        gap: { xs: '16px', md: '32px' },
      }}
    >
      {/* Order number and back button */}
      <Stack
        direction="row"
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#404040',
            fontSize: { xs: '14px', md: '18px' },
            fontWeight: 600,
            fontFamily: 'Jost, sans-serif',
            textTransform: 'capitalize',
          }}
        >
          Order no:{' '}
          <span style={{ color: '#172B4D' }}>{order?.shortId || order_id}</span>
        </Typography>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            fontSize: { xs: '11px', md: '14px' },
            fontWeight: 700,
            fontFamily: 'Jost, sans-serif',
            border: `1px solid ${environment.mainColor}`,
            textTransform: 'capitalize',
            color: environment.mainColor,
            py: { xs: 0.5, md: 1 },
            px: { xs: 1, md: 2 },
            '&:hover': {
              backgroundColor: environment.mainColor,
              color: 'white',
              border: `1px solid ${environment.mainColor}`,
            },
          }}
        >
          Back to Orders
        </Button>
      </Stack>

      {/* Summary Section (existing) */}
      <Stack
        sx={{
          borderRadius: '8px',
          padding: { xs: '8px', md: '10px' },
          backgroundColor: '#F3F6F9',
          width: '100%',
          gap: { xs: 1, md: 2 },
          mt: { xs: '20px', md: '40px' },
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Typography
              sx={{
                color: '#3C4242',
                fontSize: { xs: '11px', md: '14px' },
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Order Date :{' '}
              <span style={{ color: '#333333' }}>
                {created_at ? formatToLocalTime(created_at) : ''}
              </span>
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography
              sx={{
                color: '#3C4242',
                fontSize: { xs: '11px', md: '14px' },
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Order Status:
            </Typography>
            {statusLoaded ? (
              <OrderStatusBadge
                order={liveOrder || order}
                mappedStatus={mappedStatus}
              />
            ) : null}
          </Grid>
          {order?.deliveryOption === 'pickup' ? (
            <Grid item xs={12} sm={4}>
              <Typography
                variant="p"
                sx={{
                  color: '#3C4242',
                  fontSize: { xs: '11px', md: '14px' },
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Picked Up:{' '}
                <span style={{ color: '#333333' }}>
                  {' '}
                  {order?.store?.businessName}{' '}
                </span>
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12} sm={4}>
              <Typography
                variant="p"
                sx={{
                  color: '#3C4242',
                  fontSize: { xs: '11px', md: '14px' },
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Delivery address:{' '}
                <span style={{ color: '#333333', display: 'inline-block' }}>
                  {/** Render delivery_address object in a friendly format */}
                  {order?.delivery_address ? (
                    <span>
                      <div style={{ lineHeight: 1.2 }}>
                        {order.delivery_address.primaryLine ||
                          order.delivery_address.displayName}
                      </div>
                      <div
                        style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}
                      >
                        {order.delivery_address.city || ''}
                        {order.delivery_address.state
                          ? `, ${order.delivery_address.state}`
                          : ''}
                        {order.delivery_address.zipCode ? (
                          <span>
                            {' '}
                            <strong>{order.delivery_address.zipCode}</strong>
                          </span>
                        ) : null}
                      </div>
                    </span>
                  ) : (
                    <span>N/A</span>
                  )}
                </span>
              </Typography>
            </Grid>
          )}
          {!order?.deliveryOption === 'pickup' && (
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography
                sx={{
                  color: '#3C4242',
                  fontSize: { xs: '11px', md: '14px' },
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Estimated Delivery Date :{' '}
                <span style={{ color: '#333333' }}>
                  {estimated_delivery_date
                    ? formatToLocalTime(estimated_delivery_date)
                    : 'Not Available'}
                </span>
              </Typography>
            </Grid>
          )}{' '}
          {order?.is_scheduling && (
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography
                sx={{
                  color: '#3C4242',
                  fontSize: { xs: '11px', md: '14px' },
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Scheduled At :{' '}
                <span style={{ color: '#333333' }}>
                  {formatToLocalTime(order?.scheduled_at)}
                </span>
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <Typography
              sx={{
                color: '#3C4242',
                fontSize: { xs: '11px', md: '14px' },
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Payment Method :{' '}
              <span style={{ color: '#333333', textTransform: 'capitalize' }}>
                {displayPaymentMethod}
              </span>
            </Typography>
          </Grid>
        </Grid>
        {isScheduling && deliveryOption === 'delivery' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              marginTop: '12px',
              padding: '12px 14px',
              backgroundColor: '#FEF3C7',
              borderRadius: '10px',
              borderLeft: `4px solid ${environment.warningColor || '#F59E0B'}`,
              gridColumn: { xs: 'span 1', md: '1 / -1' },
              width: '100%',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: environment.warningColor || '#F59E0B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: 'Jost',
                }}
              >
                i
              </Typography>
            </Box>
            <Typography
              sx={{
                color: '#92400E',
                fontFamily: 'Jost',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: 1.6,
                flex: 1,
              }}
            >
              <strong>Delivery Note:</strong> An additional 45 min - 1 hour will
              be added to your scheduled time to ensure fresh delivery
            </Typography>
          </Box>
        )}
      </Stack>
      {/* Enhanced Progress Bar */}
      {statusLoaded && !isCancelled && !hasRefunds && (
        <Box
          sx={{
            mt: { xs: '32px', md: '60px' },
            mb: { xs: '40px', md: '80px' },
            width: '100%',
            maxWidth: deliveryOption === 'delivery' ? '1500px' : '700px',
            mx: 'auto',
            position: 'relative',
            px: { xs: '16px', sm: '24px' },
            py: { xs: '24px', sm: '32px' },
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            border: '1px solid #E5E7EB',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Progress Bar Header */}
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: '16px', md: '32px' },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '13px', md: '18px' },
                fontWeight: 700,
                color: '#172B4D',
                fontFamily: 'Jost, sans-serif',
                mb: { xs: '4px', md: '8px' },
              }}
            >
              {deliveryOption === 'delivery'
                ? 'Delivery Progress'
                : 'Order Progress'}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '10px', md: '14px' },
                color: '#6B7280',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Track your order status in real-time
            </Typography>
          </Box>

          {/* Step Labels Above Progress Bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'flex-start', md: 'space-between' },
              alignItems: { xs: 'flex-start', md: 'center' },
              mb: { xs: '12px', md: '20px' },
              gap: {
                xs: '8px',
                md: deliveryOption === 'delivery' ? '0px' : '8px',
              },
            }}
          >
            {ORDER_STEPS.map((step, idx) => {
              const isActive = idx <= currentStep;
              const isCurrent = idx === currentStep;
              return (
                <Box
                  key={`label-${step.key}`}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    alignItems: { xs: 'center', md: 'center' },
                    minWidth: {
                      xs: 'auto',
                      md: deliveryOption === 'delivery' ? '70px' : '95px',
                    },
                    width: { xs: '100%', md: 'auto' },
                    gap: { xs: '8px', md: '0px' },
                  }}
                >
                  {/* Step Number Badge */}
                  <Box
                    sx={{
                      width: { xs: '16px', md: '13px' },
                      height: { xs: '16px', md: '13px' },
                      borderRadius: '50%',
                      background: isActive
                        ? mappedStatus === 'cancelled'
                          ? '#FF3B30'
                          : environment.mainColor
                        : '#D1D5DB',
                      color: '#fff',
                      fontSize: { xs: '7px', md: '8px' },
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Jost, sans-serif',
                      mb: { xs: '0px', md: '6px' },
                      flexShrink: 0,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  >
                    {idx + 1}
                  </Box>
                  {/* Enhanced Label */}
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '10px',
                        md: deliveryOption === 'delivery' ? '11px' : '12px',
                      },
                      color: isActive
                        ? mappedStatus === 'cancelled'
                          ? '#FF3B30'
                          : environment.mainColor
                        : '#6B7280',
                      fontWeight: isCurrent ? 700 : isActive ? 600 : 500,
                      textAlign: { xs: 'left', md: 'center' },
                      fontFamily: 'Jost, sans-serif',
                      letterSpacing: '0.3px',
                      lineHeight: 1,
                      flex: { xs: 1, md: 'none' },
                      transition: 'all 0.3s ease',
                      textShadow: isCurrent
                        ? '0 1px 2px rgba(0,0,0,0.1)'
                        : 'none',
                      whiteSpace: { xs: 'normal', md: 'nowrap' },
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Progress Bar Section */}
          <Box
            sx={{
              position: 'relative',
              mb: '24px',
              display: { xs: 'none', md: 'block' },
            }}
          >
            {/* Enhanced Line behind dots */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '16px',
                right: '16px',
                height: '4px',
                background: 'linear-gradient(90deg, #F3F4F6 0%, #E5E7EB 100%)',
                borderRadius: '4px',
                zIndex: 1,
                transform: 'translateY(-50%)',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
              }}
            />
            {/* Enhanced Colored progress line with gradient */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '16px',
                height: '4px',
                width: `${Math.max(
                  8,
                  (currentStep / (ORDER_STEPS.length - 1)) * (100 - 4.8)
                )}%`,
                background:
                  mappedStatus === 'cancelled'
                    ? 'linear-gradient(90deg, #FF3B30 0%, #FF6B6B 100%)'
                    : `linear-gradient(90deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`,
                borderRadius: '4px',
                zIndex: 2,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'translateY(-50%)',
                boxShadow:
                  mappedStatus === 'cancelled'
                    ? '0 4px 12px rgba(255, 59, 48, 0.3)'
                    : `0 4px 12px ${environment.mainColor}40`,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  right: '-2px',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: 'inherit',
                  transform: 'translateY(-50%)',
                  boxShadow: 'inherit',
                },
              }}
            />
            {/* Enhanced Dots */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 3,
                gap: deliveryOption === 'delivery' ? '0px' : '8px',
              }}
            >
              {ORDER_STEPS.map((step, idx) => {
                const isActive = idx <= currentStep;
                const isCurrent = idx === currentStep;
                const isCompleted = idx < currentStep;
                return (
                  <Box
                    key={`dot-${step.key}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: deliveryOption === 'delivery' ? '70px' : '95px',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: isActive ? 'translateY(-2px)' : 'none',
                      },
                    }}
                  >
                    {/* Enhanced Dot with Icons */}
                    <Box
                      sx={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: isActive
                          ? mappedStatus === 'cancelled'
                            ? 'linear-gradient(135deg, #FF3B30 0%, #FF6B6B 100%)'
                            : `linear-gradient(135deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`
                          : 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                        border: `2px solid ${isActive ? '#fff' : '#F9FAFB'}`,
                        boxShadow: isCurrent
                          ? mappedStatus === 'cancelled'
                            ? '0 4px 12px rgba(255,59,48,0.25), 0 2px 6px rgba(255,59,48,0.15)'
                            : `0 4px 12px ${environment.mainColor}40, 0 2px 6px ${environment.mainColor}25`
                          : isActive
                          ? '0 2px 8px rgba(0,0,0,0.1)'
                          : '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 4,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                        '&::before': isCurrent
                          ? {
                              content: '""',
                              position: 'absolute',
                              top: '-6px',
                              left: '-6px',
                              right: '-6px',
                              bottom: '-6px',
                              borderRadius: '50%',
                              background:
                                mappedStatus === 'cancelled'
                                  ? 'rgba(255, 59, 48, 0.1)'
                                  : `${environment.mainColor}15`,
                              animation: 'pulse 2s infinite',
                            }
                          : {},
                      }}
                    >
                      {/* Step Icon or Checkmark */}
                      {isCompleted ? (
                        <Box
                          sx={{
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: 'bold',
                          }}
                        >
                          ✓
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: isActive ? '#fff' : '#9CA3AF',
                            transition: 'all 0.3s ease',
                            opacity: isCurrent ? 1 : 0.8,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Mobile Vertical Progress Bar */}
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'relative',
              mb: '24px',
              px: '16px',
            }}
          >
            {/* Vertical Line */}
            <Box
              sx={{
                position: 'absolute',
                left: '35px',
                top: '24px',
                bottom: '24px',
                width: '4px',
                background: 'linear-gradient(180deg, #F3F4F6 0%, #E5E7EB 100%)',
                borderRadius: '4px',
                zIndex: 1,
              }}
            />
            {/* Colored Progress Line */}
            <Box
              sx={{
                position: 'absolute',
                left: '35px',
                top: '24px',
                width: '4px',
                height: `${(currentStep / (ORDER_STEPS.length - 1)) * 100}%`,
                background:
                  mappedStatus === 'cancelled'
                    ? 'linear-gradient(180deg, #FF3B30 0%, #FF6B6B 100%)'
                    : `linear-gradient(180deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`,
                borderRadius: '4px',
                zIndex: 2,
                transition: 'height 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            {/* Vertical Steps */}
            {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {ORDER_STEPS.map((step, idx) => {
                const isActive = idx <= currentStep;
                const isCurrent = idx === currentStep;
                const isCompleted = idx < currentStep;
                return (
                  <Box
                    key={`mobile-${step.key}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      position: 'relative',
                      zIndex: 3,
                    }}
                  >
                  
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: isActive
                          ? mappedStatus === 'cancelled'
                            ? 'linear-gradient(135deg, #FF3B30 0%, #FF6B6B 100%)'
                            : `linear-gradient(135deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`
                          : 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                        border: `2px solid ${isActive ? '#fff' : '#F9FAFB'}`,
                        boxShadow: isCurrent
                          ? mappedStatus === 'cancelled'
                            ? '0 4px 12px rgba(255,59,48,0.25), 0 2px 6px rgba(255,59,48,0.15)'
                            : `0 4px 12px ${environment.mainColor}40, 0 2px 6px ${environment.mainColor}25`
                          : isActive
                          ? '0 2px 8px rgba(0,0,0,0.1)'
                          : '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isCurrent ? 'scale(1.1)' : 'scale(1)',
                      }}
                    >
                      {isCompleted ? (
                        <Box
                          sx={{
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 'bold',
                          }}
                        >
                          ✓
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: isActive ? '#fff' : '#9CA3AF',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      )}
                    </Box>
                
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          color: isActive
                            ? mappedStatus === 'cancelled'
                              ? '#FF3B30'
                              : environment.mainColor
                            : '#6B7280',
                          fontWeight: isCurrent ? 700 : isActive ? 600 : 500,
                          fontFamily: 'Jost, sans-serif',
                          mb: '4px',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {isCurrent && (
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color:
                              mappedStatus === 'cancelled'
                                ? '#FF3B30'
                                : environment.mainColor,
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {step.content}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box> */}
          </Box>

          {/* Content box for current step below progress bar - Desktop Only */}
          {ORDER_STEPS.map((step, idx) => {
            const isCurrent = idx === currentStep;
            return isCurrent ? (
              <Box
                key={`content-${step.key}`}
                sx={{
                  mt: '20px',
                  mb: '8px',
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  position: 'relative',
                  animation: 'fadeInUp 0.5s ease-out',
                }}
              >
                <Box
                  sx={{
                    minWidth: deliveryOption === 'delivery' ? '300px' : '320px',
                    maxWidth: deliveryOption === 'delivery' ? '400px' : '420px',
                    position: 'relative',
                  }}
                >
                  {/* Enhanced Arrow with glow */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '12px solid transparent',
                      borderRight: '12px solid transparent',
                      borderBottom: `12px solid ${
                        mappedStatus === 'cancelled'
                          ? '#FF3B3018'
                          : environment.mainColor + '18'
                      }`,
                      zIndex: 5,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                    }}
                  />
                  <Box
                    sx={{
                      background:
                        mappedStatus === 'cancelled'
                          ? 'linear-gradient(135deg, #FF3B3015 0%, #FF3B3025 100%)'
                          : `linear-gradient(135deg, ${environment.mainColor}15 0%, ${environment.mainColor}25 100%)`,
                      border: `2px solid ${
                        mappedStatus === 'cancelled'
                          ? '#FF3B30'
                          : environment.mainColor
                      }`,
                      borderRadius: '16px',
                      padding: '20px',
                      textAlign: 'center',
                      boxShadow:
                        mappedStatus === 'cancelled'
                          ? '0 8px 25px rgba(255,59,48,0.15), 0 3px 10px rgba(255,59,48,0.1)'
                          : `0 8px 25px ${environment.mainColor}25, 0 3px 10px ${environment.mainColor}15`,
                      fontWeight: 600,
                      color:
                        mappedStatus === 'cancelled'
                          ? '#FF3B30'
                          : environment.mainColor,
                      fontSize: '14px',
                      fontFamily: 'Jost, sans-serif',
                      lineHeight: 1.4,
                      position: 'relative',
                      backdropFilter: 'blur(10px)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '14px',
                        background: 'rgba(255,255,255,0.7)',
                        zIndex: -1,
                      },
                    }}
                  >
                    {step.content}
                  </Box>
                </Box>
              </Box>
            ) : null;
          })}
        </Box>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 0.7;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.4;
            }
            100% {
              transform: scale(1);
              opacity: 0.7;
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      {/* Loyalty Rewards Section */}
      {order?.loyalty && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '900px',
            mx: 'auto',
            mb: { xs: '40px', md: '80px' },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '18px', md: '24px' },
              color: '#172B4D',
              fontFamily: 'Jost, sans-serif',
              mb: { xs: '16px', md: '24px' },
              textAlign: 'center',
            }}
          >
            <span role="img" aria-label="loyalty">
              🎁
            </span>{' '}
            Loyalty Rewards Applied
          </Typography>
          {(() => {
            const loyaltyItem = order.loyalty;
            // Find the corresponding reward tier from loyaltySettings
            const rewardTier = order?.store?.loyaltySettings?.rewardTiers?.find(
              (tier) => tier.id === loyaltyItem.rewardTierId
            );

            return (
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '24px',
                  mb: '16px',
                  border: `2px solid ${environment.mainColor}20`,
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: environment.mainColor,
                  },
                }}
              >
                <Stack spacing={2}>
                  {/* Reward Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        color: '#2c3e50',
                        fontFamily: 'Jost, sans-serif',
                      }}
                    >
                      {rewardTier?.name || 'Loyalty Reward'}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: loyaltyItem.used
                          ? '#28a745'
                          : '#6c757d',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        fontFamily: 'Jost, sans-serif',
                      }}
                    >
                      {loyaltyItem.used ? 'APPLIED' : 'PENDING'}
                    </Box>
                  </Box>

                  {/* Reward Details */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#7f8c8d',
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 500,
                          }}
                        >
                          Points Used:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: environment.mainColor,
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: 'Jost, sans-serif',
                          }}
                        >
                          {loyaltyItem.points}{' '}
                          {loyaltyItem.points === 1 ? 'Point' : 'Points'}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Free Item Details */}
                    {loyaltyItem.rewardItemId && loyaltyItem.rewardItemName && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            p: 2,
                            mt: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: '#495057',
                              fontFamily: 'Jost, sans-serif',
                              fontWeight: 500,
                            }}
                          >
                            <span role="img" aria-label="free item">
                              🎯
                            </span>{' '}
                            Free Item Selected:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              color: '#2c3e50',
                              fontFamily: 'Jost, sans-serif',
                              fontWeight: 600,
                              mt: 0.5,
                            }}
                          >
                            {loyaltyItem.rewardItemName}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Return Status */}
                  {loyaltyItem.returned && (
                    <Box
                      sx={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '8px',
                        p: 2,
                        mt: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#856404',
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        <span role="img" aria-label="warning">
                          ⚠️
                        </span>{' '}
                        This reward was returned on{' '}
                        {loyaltyItem.returnedAt
                          ? new Date(
                              loyaltyItem.returnedAt
                            ).toLocaleDateString()
                          : 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Gift Card Section */}
      {activeOrder?.has_giftcard_payment && activeOrder?.giftcard_details && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '900px',
            mx: 'auto',
            mb: { xs: '40px', md: '80px' },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '18px', md: '24px' },
              color: '#172B4D',
              fontFamily: 'Jost, sans-serif',
              mb: { xs: '16px', md: '24px' },
              textAlign: 'center',
            }}
          >
            <span role="img" aria-label="gift card">
              🎁
            </span>{' '}
            Gift Card Applied
          </Typography>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              border: `2px solid ${environment.mainColor}20`,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#7f8c8d',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Card Number:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#172B4D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                  }}
                >
                  {formatGiftCardMasked(
                    activeOrder.giftcard_details?.cardDetails?.card?.last4
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#7f8c8d',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Amount Used:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: environment.mainColor,
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  -{environment.currency}
                  {formatPriceFromCents(
                    activeOrder.giftcard_amount_used ||
                      activeOrder.giftcard_details?.amountMoney?.amount ||
                      0
                  )}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Order Items Section */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          mb: { xs: '24px', md: '60px' },
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '18px', md: '24px' },
            color: '#172B4D',
            fontFamily: 'Jost, sans-serif',
            marginY: { xs: '16px', md: '24px' },
            textAlign: 'center',
          }}
        >
          Order Items
        </Typography>
        {order_items.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              mb: '16px',
              border: '1px solid #F0F0F0',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '20px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <img
                  src={item.image || item.img || item.imageUrl || logo}
                  alt={item.name || item.title}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    objectFit: 'cover',
                    border: '3px solid #F8F9FA',
                    background: '#fff',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: environment.mainColor,
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 700,
                    fontFamily: 'Jost, sans-serif',
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  {item.quantity}
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '14px', md: '18px' },
                    color: '#172B4D',
                    fontFamily: 'Jost, sans-serif',
                    mb: '6px',
                    lineHeight: 1.2,
                  }}
                >
                  {item.name || item.title}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: '12px',
                    gap: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#F8F9FA',
                      borderRadius: '8px',
                      padding: '6px 12px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#666666',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 500,
                        mr: '4px',
                      }}
                    >
                      Subtotal:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#172B4D',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {environment.currency}
                      {formatPriceFromCents(item?.calculatedPrice)}
                    </Typography>
                  </Box>
                </Box>

                {/* Display modifiers for this specific item only */}
                {item?.modifiers && item.modifiers.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: '#FAFBFC',
                      borderRadius: '12px',
                      padding: '16px',
                      mb: '16px',
                      border: '1px solid #E9ECEF',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '12px', md: '14px' },
                        color: '#495057',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        mb: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Box
                        sx={{
                          width: '4px',
                          height: '16px',
                          backgroundColor: environment.mainColor,
                          borderRadius: '2px',
                        }}
                      />
                      Customizations
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {item.modifiers.map((modifier, modIndex) => (
                        <Box
                          key={modifier.uid || modIndex}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            border: '1px solid #E9ECEF',
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                color: '#172B4D',
                                fontFamily: 'Jost, sans-serif',
                                fontWeight: 500,
                              }}
                            >
                              {modifier.name}
                            </Typography>
                            {modifier.quantity && modifier.quantity !== '1' && (
                              <Typography
                                sx={{
                                  fontSize: '11px',
                                  color: '#6C757D',
                                  fontFamily: 'Jost, sans-serif',
                                  fontStyle: 'italic',
                                }}
                              >
                                Qty: {modifier.quantity}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: environment.mainColor,
                              fontFamily: 'Jost, sans-serif',
                              fontWeight: 600,
                            }}
                          >
                            +{environment.currency}
                            {formatPriceFromCents(
                              modifier.totalPriceMoney?.amount ||
                                modifier.basePriceMoney?.amount ||
                                0
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Item Total */}
                <Box
                  sx={{
                    backgroundColor: `${environment.mainColor}10`,
                    borderRadius: '12px',
                    padding: { xs: '12px', md: '16px' },
                    border: `2px solid ${environment.mainColor}30`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '13px', md: '16px' },
                        color: '#172B4D',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      Item Total
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '14px', md: '18px' },
                        color: environment.mainColor,
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {environment.currency}
                      {formatPriceFromCents(
                        (item?.calculatedPrice || 0) +
                          (item?.modifiers?.reduce(
                            (sum, modifier) =>
                              sum +
                              (modifier.totalPriceMoney?.amount ||
                                modifier.basePriceMoney?.amount ||
                                0),
                            0
                          ) || 0)
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Order Summary Section */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          mb: { xs: '32px', md: '60px' },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            padding: { xs: '20px', md: '32px' },
            border: '1px solid #E5E7EB',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: `linear-gradient(90deg, ${environment.mainColor}, ${environment.mainColor}80)`,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: '28px',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                width: '48px',
                height: '48px',
                backgroundColor: `${environment.mainColor}15`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                role="img"
                aria-label="Order summary"
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: environment.mainColor,
                }}
              >
                📋
              </span>
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '24px',
                color: '#172B4D',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Order Summary
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Items Total */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: '#F8F9FA',
                borderRadius: '12px',
                border: '1px solid #E9ECEF',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#495057',
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: environment.mainColor,
                    borderRadius: '50%',
                  }}
                />
                Items Total
              </Typography>
              <Typography
                sx={{
                  fontSize: '18px',
                  color: '#172B4D',
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 700,
                }}
              >
                {environment.currency}
                {/* {formatPriceFromCents(
                  order_items.reduce(
                    (total, item) =>
                      total +
                      (item?.calculatedPrice || 0) +
                      (item?.modifiers?.reduce(
                        (sum, modifier) =>
                          sum +
                          (modifier.totalPriceMoney?.amount ||
                            modifier.basePriceMoney?.amount ||
                            0),
                        0
                      ) || 0),
                    0
                  )
                )} */}
                {formatPriceFromCents(order?.item_total)}
              </Typography>
            </Box>
            {/* total_modifiers_price */}
            {order?.total_modifiers_price !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#FEFEFE',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#6C757D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Total Customizations
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#172B4D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {environment.currency}
                  {formatPriceFromCents(order?.total_modifiers_price)}
                </Typography>
              </Box>
            )}
            {/* Order Tax */}
            {order?.order_tax !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#FEFEFE',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#6C757D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  Estimated Taxes
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{
                      cursor: 'pointer',
                      color: environment.mainColor,
                      '&:hover': {
                        color: environment.mainColor,
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => setOpenTaxesDialog(true)}
                  />
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#172B4D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {environment.currency}
                  {formatPriceFromCents(order.order_tax)}
                </Typography>
              </Box>
            )}

            {/* Tax Details Dialog */}
            <FeesDialog
              opentaxes={openTaxesDialog}
              setOpentaxes={setOpenTaxesDialog}
              orderType={order?.deliveryOption || 'pickup'}
              backendTax={formatPriceFromCents(order?.order_tax || 0)}
              taxesText={order?.order_taxes_text || ''}
              dialogType="tax"
            />

            {/* Order Tip */}
            {order?.order_tips !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#FEFEFE',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#6C757D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Tip
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',

                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {environment.currency}
                  {formatPriceFromCents(order.order_tips)}
                </Typography>
              </Box>
            )}
            {/* Order Delivery Fee */}
            {order?.service_charges !== 0 &&
              deliveryOption !== 'pickup' &&
              deliveryOption !== 'dinein' && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#FEFEFE',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#6C757D',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Delivery Fee
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',

                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {environment.currency}
                    {formatPriceFromCents(
                      order.service_charges - order?.store?.online_fee
                    )}
                  </Typography>
                </Box>
              )}
            {order?.store.online_fee !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#FEFEFE',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#6C757D',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Online Ordering Fee
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',

                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {environment.currency}
                  {formatPriceFromCents(order?.store?.online_fee)}
                </Typography>
              </Box>
            )}
            {/* Order Discount */}
            {order?.order_discount !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#FFF3CD',
                  border: '1px solid #FFEAA7',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#856404',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Discount
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#856404',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  -{environment.currency}
                  {formatPriceFromCents(order?.order_discount)}
                </Typography>
              </Box>
            )}
            {/* Gift Card Applied */}
            {activeOrder?.has_giftcard_payment &&
              (activeOrder?.giftcard_amount_used ?? 0) > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#E8F5E9',
                    border: '1px solid #C8E6C9',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#2E7D32',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Gift Card Applied
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#2E7D32',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    -{environment.currency}
                    {formatPriceFromCents(activeOrder.giftcard_amount_used)}
                  </Typography>
                </Box>
              )}
          </Box>

          {/* Order Total */}
          <Divider
            sx={{
              my: '24px',
              borderColor: '#E5E7EB',
              borderWidth: '1px',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              backgroundColor: `${environment.mainColor}08`,
              borderRadius: '16px',
              border: `2px solid ${environment.mainColor}20`,
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                color: '#172B4D',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: environment.mainColor,
                  borderRadius: '50%',
                }}
              />
              Total Amount
            </Typography>
            {(() => {
              // Base total in cents from the order record
              const rawTotalCents =
                order_total ?? activeOrder?.order_total ?? 0;

              // Amounts in cents related to gift card usage
              const giftCardUsedCents = activeOrder?.giftcard_amount_used ?? 0;
              const otherPaymentCents =
                activeOrder?.other_payment_amount_used ?? 0;

              // When a gift card is used, show what the customer actually paid
              // out of pocket (non-gift-card portion).
              let effectiveTotalCents = rawTotalCents;
              if (
                activeOrder?.has_giftcard_payment &&
                (giftCardUsedCents ?? 0) > 0
              ) {
                if (otherPaymentCents > 0) {
                  // Backend explicitly tracked the non-gift-card amount
                  effectiveTotalCents = otherPaymentCents;
                } else {
                  // Fallback: subtract gift card amount from order total
                  effectiveTotalCents = Math.max(
                    0,
                    rawTotalCents - giftCardUsedCents
                  );
                }
              }

              return (
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: environment.mainColor,
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {environment.currency}
                  {formatPriceFromCents(effectiveTotalCents)}
                </Typography>
              );
            })()}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default OrderHistoryDetails;
